import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postRequest } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"

const MODE_VISUALIZE = "visualize_data"
const MODE_SEND      = "send_by_email"

const STEP_TYPE = "type"
const STEP_MODE = "mode"

// Two-step React export modal.
//   Step 1 — pick what to export (library only / with lines)
//   Step 2 — pick delivery (visualize in tab / send by email)
// Submits to Quotes::QlineServiceBlueprintsController#csv; the server creates
// the Export and dispatches Exports::Process.
const CsvExportModal = ({
  isOpen,
  onClose,
  csvUrl,
  search,
  ids,
  count,
  defaultEmail,
}) => {
  const [step,       setStep]       = useState(STEP_TYPE)
  const [withLines,  setWithLines]  = useState(false)
  const [mode,       setMode]       = useState(MODE_VISUALIZE)
  const [recipient,  setRecipient]  = useState(defaultEmail || "")
  const [submitting, setSubmitting] = useState(false)

  // Reset to step 1 every time the modal opens.
  useEffect(() => {
    if (!isOpen) return
    setStep(STEP_TYPE)
    setWithLines(false)
    setMode(MODE_VISUALIZE)
    setRecipient(defaultEmail || "")
  }, [isOpen, defaultEmail])

  const handleSubmit = () => {
    if (submitting) return
    setSubmitting(true)

    const body = {
      search,
      ids,
      with_lines:  withLines,
      export_mode: mode,
      recipient:   mode === MODE_SEND ? recipient : undefined,
    }

    postRequest({ url: csvUrl, body })
      .then(response => {
        if (!response || response.success === false) {
          flashAlert(I18n.t("quotes.qline_service_blueprints.csv.export_failed"))
          return
        }
        if (response.send_by_email) {
          flashNotice(I18n.t("quotes.qline_service_blueprints.csv.email_pending"))
        } else if (response.visualize_url) {
          window.open(response.visualize_url, "_blank")
        }
        onClose()
      })
      .catch(() => flashAlert(I18n.t("quotes.qline_service_blueprints.index.network_error")))
      .finally(() => setSubmitting(false))
  }

  const footer = step === STEP_TYPE ? (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("quotes.qline_service_blueprints.csv.cancel")}</Button>
        <Button variant="primary" onClick={() => setStep(STEP_MODE)}>
          {I18n.t("quotes.qline_service_blueprints.csv.continue")}
          <FontAwesomeIcon icon="arrow-right" />
        </Button>
      </div>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={() => setStep(STEP_TYPE)}>
        <FontAwesomeIcon icon="arrow-left" />
        {I18n.t("quotes.qline_service_blueprints.csv.back")}
      </Button>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClose}>{I18n.t("quotes.qline_service_blueprints.csv.cancel")}</Button>
        <Button
          variant  = "primary"
          onClick  = {handleSubmit}
          disabled = {submitting || (mode === MODE_SEND && !recipient)}
        >
          <FontAwesomeIcon icon={submitting ? "spinner" : "file-export"} spin={submitting} />
          {I18n.t("quotes.qline_service_blueprints.csv.run_export")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {I18n.t("quotes.qline_service_blueprints.csv.eyebrow", { step: step === STEP_TYPE ? "1 / 2" : "2 / 2" })}
      title   = {I18n.t("quotes.qline_service_blueprints.csv.title")}
      footer  = {footer}
      size    = "sm"
    >
      <p className="text-sm text-slate-600">
        <span className="font-semibold text-slate-900 tabular-nums">{count}</span>{" "}
        {I18n.t("quotes.qline_service_blueprints.csv.count_to_export", { count })}
        {search ? <> {I18n.t("quotes.qline_service_blueprints.csv.for_search")} <span className="font-medium text-slate-900">« {search} »</span></> : null}.
      </p>

      {step === STEP_TYPE && (
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type      = "radio"
              name      = "export_type"
              value     = "groups"
              checked   = {!withLines}
              onChange  = {() => setWithLines(false)}
              className = "mt-1"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">{I18n.t("quotes.qline_service_blueprints.csv.services_only")}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {I18n.t("quotes.qline_service_blueprints.csv.services_only_description")}
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type      = "radio"
              name      = "export_type"
              value     = "lines"
              checked   = {withLines}
              onChange  = {() => setWithLines(true)}
              className = "mt-1"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">{I18n.t("quotes.qline_service_blueprints.csv.services_with_lines")}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {I18n.t("quotes.qline_service_blueprints.csv.services_with_lines_description")}
              </div>
            </div>
          </label>
        </div>
      )}

      {step === STEP_MODE && (
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type      = "radio"
              name      = "export_mode"
              value     = {MODE_VISUALIZE}
              checked   = {mode === MODE_VISUALIZE}
              onChange  = {() => setMode(MODE_VISUALIZE)}
              className = "mt-1"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">{I18n.t("quotes.qline_service_blueprints.csv.visualize_label")}</div>
              <div className="text-xs text-slate-500 mt-0.5">{I18n.t("quotes.qline_service_blueprints.csv.visualize_description")}</div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type      = "radio"
              name      = "export_mode"
              value     = {MODE_SEND}
              checked   = {mode === MODE_SEND}
              onChange  = {() => setMode(MODE_SEND)}
              className = "mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">{I18n.t("quotes.qline_service_blueprints.csv.email_label")}</div>
              <div className="text-xs text-slate-500 mt-0.5">{I18n.t("quotes.qline_service_blueprints.csv.email_description")}</div>
              {mode === MODE_SEND && (
                <input
                  type         = "email"
                  value        = {recipient}
                  onChange     = {event => setRecipient(event.target.value)}
                  placeholder  = {I18n.t("quotes.qline_service_blueprints.csv.email_placeholder")}
                  required
                  autoComplete = "off"
                  className    = "mt-2 w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:border-brand-500"
                />
              )}
            </div>
          </label>
        </div>
      )}
    </Modal>
  )
}

export default CsvExportModal
